import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  authService,
  isEmailVerificationRequiredResponse,
  type LoginCredentials,
  type LoginResult,
} from '../services/auth.service';
import type { User } from '../types/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load user data on mount
  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUser = async () => {
    try {
      // First check if we have a token
      const token = authService.getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      // Try to get stored user first to avoid unnecessary API call
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        setUser(storedUser);
        setLoading(false);
        // Optionally refresh in background
        authService.getCurrentUser()
          .then(freshUser => setUser(freshUser))
          .catch(() => {
            // If refresh fails, keep the stored user
          });
        return;
      }

      // No stored user, fetch from API
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Clear invalid auth data
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      const response = await authService.login(credentials);

      if (isEmailVerificationRequiredResponse(response)) {
        setUser(null);
        navigate('/verify-email', {
          state: {
            email: response.email,
            verificationChallengeId: response.verificationChallengeId,
            fromLogin: true,
            message: response.message,
          },
        });
        return 'verification_required';
      }

      setUser(response.user);
      navigate('/dashboard');
      return 'authenticated';
    } catch (error) {
      setUser(null);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      navigate('/login');
    }
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
      navigate('/login');
    }
  };

  const isAuthenticated = !!user && authService.isAuthenticated();

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
